// B-56 — the operator's observation when they attend an alert. The macro's column AK
// OBSERVACIONES, which is `closing_alerts.resolution_note`.
//
// One place, used by the Alertas feed and by the closing detail dialog, because the two surfaces
// must send the same thing: `resolveCierreAlert` already took a `note` and both callers were
// passing `null`, i.e. closing a difference with no explanation of it.
//
// **A hook that hands back a trigger and a form, not a single component**, because of where each
// half belongs on screen. The feed's card is a three-column grid whose last column is `auto`: a
// textarea placed there would stretch that column and squeeze the message beside it. So the button
// goes in the actions column and the form spans the card underneath — two positions, one piece of
// state, one call site.
//
// Three decisions are load-bearing, and each one is the server's behaviour rather than a
// preference:
//
//   * **The note is optional.** The mural is explicit — *"Si no se escribe ninguna observación
//     tiene que quedar la opción para continuar el proceso con esa diferencia pendiente"* — and
//     `optionalProse` stores a blank one as `null`. So the confirm button never waits for text.
//     `maxLength` is the server's own 1.000, so a long note is cut while it is being typed instead
//     of earning a 400 after the click.
//   * **It asks before it sends.** Attending is irreversible: `closing_alerts` is append-only and
//     a second attempt earns 409 *"is already RESOLVED_BY_USER and is immutable"*. The old feed
//     could offer "Reabrir" only because its "Atendida" never left the browser. Since the
//     observation needs a box anyway, that box IS the confirmation — no extra click for it.
//   * **A refusal keeps the form open.** Every 400/403/404/409 here is written for the operator
//     ("ese cierre está cerrado", "alguien ya la cerró"), and closing the form on error would throw
//     away both the message and what they had typed.
//
// **B-115 — the adjustment to the next day's opening balance, automatic.** An alert the backend
// serves as `adjustable` (Bancamía's vault descuadre: the opening balance is yesterday's theoretical
// close, so the difference repeats every day until it is adjusted) is attended WITH the adjustment:
// by default the difference the alert read, said in words, nothing to fill in. "Ajustar otro monto"
// opens a field for the rare other amount ("Usar la diferencia" goes back). Either way a dialog says
// what the next day's opening balance will change by before anything is sent — it cannot be undone.
// The amount travels as written under es-CO; the backend parses it under the client's locale.
import { useState } from "react";
import { Check, MessageSquarePlus, Pencil, Scale, X } from "lucide-react";
import {
  Button,
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  Input,
  Spinner,
  Textarea,
} from "@mantis-sas/ui";

import { resolveCierreAlert, updateCierreAlertNote } from "../../../ApiCalls/mantisBusiness";
import { correctionError } from "../../../utils/cierreCorrections";
import { montoEscrito } from "../../../utils/cierreCuadreDia";
import {
  montoLegible, montoMientrasSeEscribe, montoParaEscribir, numeroDeCelda, tonoDeAjuste,
} from "../../../utils/cierrePanel";
import { guia } from "../../../utils/cierreTours";
import { BotonGuia } from "./GuiaPantalla";

// Matches the panel's own inputs rather than the primitive's hardcoded `bg-white`, same as
// `CierreManualDialog`'s `CAMPO`.
const CAMPO = "bg-card text-brinks-foreground";

const GUIA = "Por qué hay esta diferencia — queda registrado con tu nombre";

/**
 * @param {object} args
 * @param {string} args.alertaId    the alert to close
 * @param {string} args.closingId   its closing — the server checks the two agree
 * @param {function} args.onAtendida  called with the stored row (`data.alert`) on success
 * @param {boolean} [args.compacto]   denser trigger, for the detail dialog's narrow column
 * @param {?{into: string, suggested: ?string}} [args.ajustable]  B-115: the backend's `adjustable`
 * @returns {{abierto: boolean, abrir: function, boton: JSX.Element, formulario: JSX.Element|null}}
 */
export function useAtenderAlerta({
  alertaId, closingId, token, mantisBusinessId, onAtendida, compacto = false, ajustable = null,
}) {
  const [abierto, setAbierto] = useState(false);
  const [nota, setNota] = useState("");
  const [otroMonto, setOtroMonto] = useState(false);
  const [monto, setMonto] = useState("");
  // B-115 — the amount the confirmation shows, as it will be sent; null while it is closed.
  const [porConfirmar, setPorConfirmar] = useState(null);
  const [enviando, setEnviando] = useState(false);
  const [error, setError] = useState("");

  // With no difference to suggest, the other amount is the only amount.
  const sugerido = numeroDeCelda(ajustable?.suggested);
  const escribeMonto = !!ajustable && (otroMonto || !sugerido);

  const abrir = () => {
    setOtroMonto(false);
    setMonto("");
    setAbierto(true);
  };

  const cerrar = () => {
    setAbierto(false);
    setNota("");
    setError("");
    setPorConfirmar(null);
  };

  const enviar = async (ajuste) => {
    setEnviando(true);
    setError("");
    // Trimmed-or-null rather than as typed. Unlike a corrected figure — which travels exactly as
    // typed because the backend parses it against the field's declared locale, and a formatter here
    // would be a second one — a note is prose, and `optionalProse` trims it anyway.
    const observacion = nota.trim() === "" ? null : nota.trim();
    const response = await resolveCierreAlert(
      token, mantisBusinessId, closingId, alertaId, observacion, ajuste,
    ).catch(() => null);
    setEnviando(false);
    setPorConfirmar(null);
    if (!response?.success) {
      setError(correctionError(response));
      return;
    }
    cerrar();
    // `data.alert` is the stored row — status, author, timestamp, and the note as it was saved. The
    // card renders that rather than what was typed, so the screen cannot disagree with the table.
    onAtendida(response.data?.alert, response.data);
  };

  // "Marcar atendida": an adjustable alert asks first, saying what the next day will change by.
  const atender = () => {
    if (!ajustable) {
      enviar(null);
      return;
    }
    const valor = escribeMonto ? montoEscrito(monto) : Number(sugerido).toFixed(2);
    if (!valor) {
      setError("Escribe el monto del ajuste: un valor en pesos distinto de cero, por ejemplo -330.000.");
      return;
    }
    setError("");
    setPorConfirmar(valor);
  };

  const montoConfirmado = numeroDeCelda(porConfirmar) || 0;

  const boton = (
    <Button
      variant="outline"
      size="sm"
      className={compacto ? "h-7 shrink-0 px-2 text-[11px]" : "h-9"}
      disabled={abierto}
      onClick={abrir}
    >
      <MessageSquarePlus className={compacto ? "h-3.5 w-3.5" : "h-4 w-4"} />
      Atender
    </Button>
  );

  const formulario = abierto ? (
    <div className="space-y-1.5 rounded-[9px] border border-brinks-border-strong bg-brinks-surface p-2.5">
      <label
        className="block text-[11px] font-semibold uppercase tracking-[0.04em] text-brinks-muted-foreground"
        htmlFor={`observacion-${alertaId}`}
      >
        Observación (opcional)
      </label>
      <Textarea
        {...guia("Observación", "Opcional: por qué queda así esta diferencia. Se guarda con tu nombre y aparece en la columna de observaciones.")}
        id={`observacion-${alertaId}`}
        className={CAMPO}
        rows={2}
        maxLength={1000}
        placeholder={GUIA}
        value={nota}
        disabled={enviando}
        onChange={(e) => setNota(e.target.value)}
      />
      {ajustable ? (
        <div className="flex items-start gap-2 rounded-[8px] border border-brinks-border bg-card px-2.5 py-2">
          <Scale aria-hidden className="mt-0.5 h-3.5 w-3.5 shrink-0 text-brinks-muted-foreground" />
          <div className="min-w-0 flex-1 space-y-1.5">
            {escribeMonto ? (
              <>
                <label htmlFor={`ajuste-${alertaId}`} className="block text-[12.5px] text-brinks-foreground">
                  Monto para ajustar el saldo de bóveda
                </label>
                <div className="flex flex-wrap items-center gap-2">
                  {/* The dots go in as it is typed, and the figure turns green (adds) or red (takes away). */}
                  <Input
                    {...guia("Otro monto", "El monto del ajuste, si no es la diferencia que leyó la alerta.")}
                    id={`ajuste-${alertaId}`}
                    className={`h-8 w-40 font-semibold tabular-nums ${CAMPO} ${tonoDeAjuste(montoEscrito(monto))}`}
                    inputMode="decimal"
                    placeholder={sugerido ? montoParaEscribir(sugerido) : "-330.000"}
                    value={monto}
                    disabled={enviando}
                    onChange={(e) => setMonto(montoMientrasSeEscribe(e.target.value))}
                  />
                  <span className="text-[11px] text-brinks-muted-foreground">Positivo suma, negativo resta.</span>
                  {sugerido ? (
                    <button
                      type="button"
                      className="text-[11.5px] font-medium text-brinks-foreground underline underline-offset-2"
                      disabled={enviando}
                      onClick={() => setOtroMonto(false)}
                    >
                      Usar la diferencia ({montoLegible(sugerido)})
                    </button>
                  ) : null}
                </div>
              </>
            ) : (
              <p className="text-[12.5px] leading-snug text-brinks-foreground">
                Al atenderla, el saldo de bóveda se ajusta en{" "}
                <span className={`font-semibold tabular-nums ${tonoDeAjuste(sugerido)}`}>
                  {sugerido > 0 ? "+" : ""}
                  {montoLegible(sugerido)}
                </span>{" "}
                para que coincida con lo contado.{" "}
                <button
                  type="button"
                  className="text-[11.5px] font-medium underline underline-offset-2"
                  disabled={enviando}
                  onClick={() => setOtroMonto(true)}
                >
                  Ajustar otro monto
                </button>
              </p>
            )}
          </div>
        </div>
      ) : null}
      {error ? (
        <p
          role="alert"
          className="rounded-[8px] border border-brinks-danger-border bg-brinks-danger-soft px-2.5 py-1.5 text-[12.5px] text-brinks-danger"
        >
          {error}
        </p>
      ) : null}
      <div className="flex flex-wrap items-center gap-2">
        {/* Not disabled on an empty note: see the header. Attending without writing anything is
            what the mural calls continuing with the difference pending. */}
        <Button size="sm" className="h-8 gap-1.5" disabled={enviando} onClick={atender}>
          {enviando ? <Spinner className="h-3.5 w-3.5" /> : <Check className="h-3.5 w-3.5" />}
          Marcar atendida
        </Button>
        <Button variant="outline" size="sm" className="h-8 gap-1.5" disabled={enviando} onClick={cerrar}>
          <X className="h-3.5 w-3.5" />
          Cancelar
        </Button>
        <span className="text-[11px] text-brinks-muted-foreground">Queda guardada y no se puede reabrir.</span>
      </div>
      {/* B-115 — said before it is sent: the next day's opening balance moves, and it cannot be undone. */}
      <Dialog open={porConfirmar !== null} onOpenChange={(abre) => (!abre && !enviando ? setPorConfirmar(null) : null)}>
        <DialogContent className="brinks-scope font-redhat max-w-md">
          <DialogHeader className="text-left">
            <div className="flex min-w-0 items-center gap-1">
              <DialogTitle className="text-[16px] font-semibold text-brinks-foreground">
                ¿Ajustar el saldo de bóveda?
              </DialogTitle>
              <BotonGuia guiaId="dialogo-ajustar-boveda" titulo="Ajustar el saldo de bóveda" texto="Al atender esta alerta se suma o se resta la diferencia al saldo de bóveda que pasa al día siguiente, para que coincida con lo contado. No se puede deshacer." />
            </div>
            <DialogDescription className="text-[13px] leading-relaxed text-brinks-muted-foreground">
              Se va a {montoConfirmado > 0 ? "sumar" : "restar"}{" "}
              <span className={`font-semibold tabular-nums ${tonoDeAjuste(montoConfirmado)}`}>
                {montoLegible(Math.abs(montoConfirmado))}
              </span>{" "}
              al saldo de bóveda para que coincida con lo contado, y el día siguiente se vuelve a revisar. La alerta
              queda atendida con tu nombre y no se puede deshacer.
            </DialogDescription>
          </DialogHeader>
          <DialogFooter className="gap-2">
            <Button variant="outline" size="sm" disabled={enviando} onClick={() => setPorConfirmar(null)}>
              Cancelar
            </Button>
            {/* Written back the Colombian way: the backend reads es-CO, where "330000.00" is not a number. */}
            <Button size="sm" className="gap-1.5" disabled={enviando} onClick={() => enviar(montoParaEscribir(porConfirmar))}>
              {enviando ? <Spinner className="h-3.5 w-3.5" /> : <Check className="h-3.5 w-3.5" />}
              Atender y ajustar
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  ) : null;

  return { abierto, abrir, boton, formulario };
}

/**
 * Changing the observation on an alert a person already attended — the same trigger-and-form pair
 * as `useAtenderAlerta`, for the same layout reasons.
 *
 * The attended row cannot be rewritten (`closing_alerts` freezes it), so the backend keeps it and
 * stores this as a later version, which every reader then serves as `resolutionNote` — the panel,
 * the feed, the detail and the Matriz's column AK. The form opens on the current observation, an
 * emptied box clears it, and the caller is handed the stored alert, never what was typed.
 *
 * @param {object} args
 * @param {object} args.alerta       the attended alert: `id`, `resolutionNote`
 * @param {string} args.closingId    its closing — the server checks the two agree
 * @param {function} args.onEditada  called with the stored row (`data.alert`) on success
 * @param {boolean} [args.compacto]  denser trigger, for the detail dialog's narrow column
 */
export function useEditarObservacion({ alerta, closingId, token, mantisBusinessId, onEditada, compacto = false }) {
  const [abierto, setAbierto] = useState(false);
  const [nota, setNota] = useState("");
  const [enviando, setEnviando] = useState(false);
  const [error, setError] = useState("");
  const alertaId = alerta?.id;

  const abrir = () => {
    setNota(alerta?.resolutionNote || "");
    setError("");
    setAbierto(true);
  };

  const cerrar = () => {
    setAbierto(false);
    setNota("");
    setError("");
  };

  const guardar = async () => {
    setEnviando(true);
    setError("");
    const observacion = nota.trim() === "" ? null : nota.trim();
    const response = await updateCierreAlertNote(token, mantisBusinessId, closingId, alertaId, observacion).catch(() => null);
    setEnviando(false);
    if (!response?.success) {
      setError(correctionError(response));
      return;
    }
    cerrar();
    onEditada(response.data?.alert, response.data);
  };

  const boton = (
    <Button
      variant="outline"
      size="sm"
      className={compacto ? "h-7 shrink-0 px-2 text-[11px]" : "h-9"}
      disabled={abierto}
      onClick={abrir}
    >
      <Pencil className={compacto ? "h-3.5 w-3.5" : "h-4 w-4"} />
      Editar observación
    </Button>
  );

  const formulario = abierto ? (
    <div className="space-y-1.5 rounded-[9px] border border-brinks-border-strong bg-brinks-surface p-2.5">
      <label
        className="block text-[11px] font-semibold uppercase tracking-[0.04em] text-brinks-muted-foreground"
        htmlFor={`editar-observacion-${alertaId}`}
      >
        Observación
      </label>
      <Textarea
        {...guia("Observación", "Cambia la observación de una alerta ya atendida. La anterior queda en el historial.")}
        id={`editar-observacion-${alertaId}`}
        className={CAMPO}
        rows={2}
        maxLength={1000}
        placeholder={GUIA}
        value={nota}
        disabled={enviando}
        onChange={(e) => setNota(e.target.value)}
      />
      {error ? (
        <p
          role="alert"
          className="rounded-[8px] border border-brinks-danger-border bg-brinks-danger-soft px-2.5 py-1.5 text-[12.5px] text-brinks-danger"
        >
          {error}
        </p>
      ) : null}
      <div className="flex flex-wrap items-center gap-2">
        <Button size="sm" className="h-8 gap-1.5" disabled={enviando} onClick={guardar}>
          {enviando ? <Spinner className="h-3.5 w-3.5" /> : <Check className="h-3.5 w-3.5" />}
          Guardar observación
        </Button>
        <Button variant="outline" size="sm" className="h-8 gap-1.5" disabled={enviando} onClick={cerrar}>
          <X className="h-3.5 w-3.5" />
          Cancelar
        </Button>
        <span className="text-[11px] text-brinks-muted-foreground">La anterior queda en el historial del cierre.</span>
      </div>
    </div>
  ) : null;

  return { abierto, boton, formulario };
}
